import { useCallback } from 'react';

import { UserPreferenceName } from '../constants';
import { useUserPreferencesStore } from '../store';
import { useUpdateUserPreferences } from './useUpdateUserPreferences';

/** The preference names of the accordions (boolean: open or closed) */
export type AccordionPreferenceName = Extract<
  UserPreferenceName,
  `ui.accordions.expanded.${string}`
>;

/**
 * Remembers whether an accordion is open, per user, in its own ACCORDION_* preference.
 * Without a name nothing is stored and `expanded` is undefined, so the accordion stays uncontrolled.
 */
export const useAccordionExpanded = (name: AccordionPreferenceName | undefined) => {
  // Selects a single boolean, so toggling one accordion doesn't re-render the others
  const stored = useUserPreferencesStore((state) =>
    name === undefined ? undefined : state.getPreference(name),
  );
  const { updateUserPreference } = useUpdateUserPreferences();

  const setExpanded = useCallback(
    (expanded: boolean) => {
      if (name === undefined) return;

      updateUserPreference(name, expanded);
    },
    [name, updateUserPreference],
  );

  return {
    expanded: stored,
    setExpanded,
  };
};
